import { useAtomValue } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";

import useWorkspacePermissions from "../../../../hooks/useWorkspacePermissions";
import useCloudPermissions from "../../../../hooks/useCloudPermissions";
import { workspaceIdAtom } from "../../../../atoms/WorkspaceAtoms";
import { tenantIDAtom } from "../../../../atoms/GlobalAtoms";
import PageAPI from "../../../api/PageAPI";
import WorkspaceAPI from "../../../api/WorkspaceAPI";

// This react query hook is used to delete a workspace's sidebar item.
const useDeleteItem = () => {
  const { wPermissions } = useWorkspacePermissions();
  const { cPermissions } = useCloudPermissions();
  const queryClient = useQueryClient();
  const workspaceID = useAtomValue(workspaceIdAtom);
  const tenantID = useAtomValue(tenantIDAtom);
  const navigate = useNavigate();
  const { pageID }: any = useParams();

  const deleteItem = async (itemId: string) => {
    if (
      !wPermissions.workspace_modify_workspace &&
      !cPermissions.cloud_modify_workspace
    ) {
      return console.log("You do not have permission to modify this workspace");
    }

    const { workspace } = queryClient.getQueryData<any>([
      "workspace",
      workspaceID,
    ]);

    const handleIsPageLink = async (item: any) => {
      if (!item?.PageID) return;

      await PageAPI.deletePage(workspaceID, item?.PageID);

      queryClient.invalidateQueries({
        queryKey: ["pages", workspaceID],
      });

      if (item?.PageID === pageID) {
        navigate(`/clouds/${tenantID}/workspaces/${workspaceID}`);
      }
    };

    const newSidebar = workspace.Sidebar.flatMap((item: any) => {
      if (item.id === itemId) {
        handleIsPageLink(item);

        return [];
      }

      if (item.Type === "Group") {
        item.Items = item.Items.flatMap((subItem: any) => {
          if (subItem.id === itemId) {
            handleIsPageLink(subItem);

            return [];
          }

          return [subItem];
        });
      }

      return [item];
    });

    const result = await WorkspaceAPI.updateWorkspace(workspaceID, {
      Sidebar: newSidebar,
    });

    queryClient.setQueryData<any>(["workspace", workspaceID], (data: any) => ({
      ...data,
      workspace: result.data.data.workspace,
    }));
  };

  return { deleteItem };
};

export default useDeleteItem;
